import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Sparkles, LogOut, Loader2, ExternalLink, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString(undefined, {year:'numeric', month:'long', day:'numeric'}); }

const PLAN_META = {
  admin: { label: 'Admin',      color: '#A06628', desc: 'Owner account — full access, no payment needed' },
  pro:   { label: 'Pro',        color: '#2D5F4F', desc: 'Monthly plan — full access to every module' },
  none:  { label: 'Not active', color: '#7A857F', desc: 'Subscribe to unlock the platform' },
};

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { api.billingMe().then((b) => { setBilling(b); setLoading(false); }).catch(() => setLoading(false)); }, []);

  async function openPortal() {
    setPortalLoading(true); setErr(null);
    try {
      const { portal_url } = await api.createPortalSession();
      window.location.href = portal_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not open the billing portal. Please try again.');
      setPortalLoading(false);
    }
  }

  const plan = billing?.plan || 'none';
  const meta = PLAN_META[plan] || PLAN_META.none;

  return (
    <div className="min-h-screen"><Navbar />
      <main className="max-w-[820px] mx-auto px-6 lg:px-8 pt-8 pb-16">
        <h1 className="font-display text-4xl tracking-tight text-[#1A2622]">Account</h1>
        <p className="mt-2 text-[13px] text-[#7A857F]">{user?.email}</p>

        <section className="mt-6 rounded-xl bg-white border border-[#E5DCC4] p-6" data-testid="account-plan-card">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Current plan</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold" style={{background:`${meta.color}1A`, color: meta.color}} data-testid="account-plan-badge">{meta.label}</span>
          </div>
          <div className="font-display text-2xl text-[#1A2622]">{loading ? 'Loading…' : meta.label} plan</div>
          <p className="mt-1 text-[12.5px] text-[#5A6660]">{meta.desc}</p>

          {billing?.current_period_end && plan === 'pro' && (
            <div className="mt-4">
              <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">{billing.cancel_at_period_end ? 'Access ends' : 'Next billing date'}</span>
              <div className="text-[13px] text-[#1A2622]" data-testid="account-period-end">{fmtDate(billing.current_period_end)}</div>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {billing?.has_billing ? (
              <button onClick={openPortal} disabled={portalLoading}
                data-testid="account-manage-billing-btn"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[13px] font-medium hover:bg-[#2D5F4F] disabled:opacity-50">
                {portalLoading ? <Loader2 size={14} className="animate-spin"/> : <CreditCard size={14}/>} Manage subscription
                <ExternalLink size={12} className="opacity-60"/>
              </button>
            ) : (
              plan !== 'admin' && <Link to={`/pricing?email=${encodeURIComponent(user?.email || '')}`} className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-[#A06628] text-white text-[13px] font-medium hover:bg-[#7A5230]" data-testid="account-upgrade-btn">
                <Sparkles size={14}/> Subscribe — $49.99/month
              </Link>
            )}
            <button onClick={logout} data-testid="account-logout-btn"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-[#E5DCC4] text-[#3A4A44] text-[13px] hover:bg-[#F5EFDD]">
              <LogOut size={13}/> Sign out
            </button>
          </div>
          {err && <div className="mt-3 text-[12px] text-[#A63A3A]">{err}</div>}
        </section>

        <section className="mt-4 rounded-xl bg-[#F5EFDD] border border-[#E5DCC4] p-5 flex items-start gap-2.5 text-[12.5px] text-[#3A4A44]">
          <Info size={14} className="text-[#A06628] shrink-0 mt-0.5"/>
          <div>
            <strong>Cancel anytime.</strong> Click &ldquo;Manage subscription&rdquo; to reach the secure Stripe portal — cancel your subscription or update your card in one click.
            You keep full access through the end of the month you paid for.
          </div>
        </section>

        <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] p-5">
          <h2 className="font-display text-base font-semibold text-[#1A2622]">Need help?</h2>
          <p className="mt-1 text-[12.5px] text-[#5A6660]">Lost your access code, want to change your email, or have a billing question?</p>
          <a href={`mailto:support@pocltc.org?subject=Account%20help%20-%20${encodeURIComponent(user?.email || '')}`}
             className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] text-[#34538F] hover:underline" data-testid="account-support-link">
            support@pocltc.org
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}
